// 149-cw-r121 (2026-08-29) — Validation helpers untuk Laporan Keuangan.
// Read-only checks — TIDAK mengubah data, hanya menandai untuk pemeriksaan manual.
import { computeExpectedNet, computeTotalReceived } from "./paymentReceipts";

/**
 * Deteksi apakah invoice adalah data test (TEST_LOC_A_*, TEST 149BE, dll).
 * Cek nama project + client + note untuk pattern TEST/DEMO/DUMMY.
 */
export function isTestData(project, extraText = "") {
  if (!project) return false;
  const hay = [
    project.project_name,
    project.wo_number,
    project.variation_order_name,
    project.client,
    project.location_id,
    extraText,
  ].filter(Boolean).join(" ").toUpperCase();
  return /\bTEST[_\s-]?LOC|\bTEST\s?\d|\bTEST[_\s]/i.test(hay) || /\bDUMMY\b/i.test(hay) || /\bDEMO\b/i.test(hay);
}

/**
 * Legacy check: entry punya "sudah diterima" (entry.date filled) tetapi TIDAK
 * punya payment_receipts. Nilai penerimaan di-assume = expected_net (legacy).
 */
export function isLegacyReceived(entry) {
  if (!entry) return false;
  const receipts = entry.payment_receipts || [];
  if (receipts.length > 0) return false;
  return !!entry.date;
}

/**
 * Kumpulkan flag validasi read-only untuk 1 invoice entry.
 * Return array of {severity, code, message}.
 * severity: "warn" (kuning) | "error" (merah)
 */
export function validateInvoice(entry) {
  const flags = [];
  const expected = computeExpectedNet(entry);
  const received = computeTotalReceived(entry);
  // 1. Pembayaran melebihi kas yang harus diterima
  if (received > expected + 1 && expected > 0) {
    flags.push({
      severity: "warn",
      code: "OVERPAY",
      message: `Pembayaran (${fmtShort(received)}) melebihi kas yang harus diterima (${fmtShort(expected)}).`,
    });
  }
  // 2. Sisa piutang negatif
  const remaining = expected - received;
  if (remaining < -1) {
    flags.push({
      severity: "warn",
      code: "NEGATIVE_REMAINING",
      message: `Sisa piutang negatif: ${fmtShort(remaining)}.`,
    });
  }
  // 3. Data invoice tanpa tanggal atau nomor valid
  if (!entry.invoice_date) {
    flags.push({
      severity: "warn",
      code: "MISSING_INVOICE_DATE",
      message: "Invoice tidak memiliki tanggal.",
    });
  }
  // 4. Status "lunas" tetapi nilai tidak cocok (dianggap lunas ketika received ≥ expected*0.99)
  const isPaid = received > 0 && expected > 0 && received + 0.01 >= expected;
  if (isPaid && Math.abs(received - expected) > 1 && received < expected) {
    flags.push({
      severity: "warn",
      code: "PAID_MISMATCH",
      message: "Status Lunas tetapi nilai penerimaan tidak sesuai.",
    });
  }
  return flags;
}

/**
 * SATU universe perhitungan penerimaan untuk Ringkasan & Pembayaran Masuk
 * (2026-06-20, user request rekonsiliasi). Aturan eksklusi:
 *  - proyek internal ("Kantor") — bukan penerimaan proyek;
 *  - klaim retensi — dihitung terpisah di tab Retensi & Collection;
 *  - data TEST/DEMO/DUMMY — disembunyikan bila hideTest aktif.
 * Read-only: baris yang dieksklusi TIDAK dihapus/diubah, hanya tidak dihitung.
 */
export function incomeExclusionReason(entry, project, { hideTest = true } = {}) {
  if (project?.project_type === "internal") return "Proyek internal (bukan penerimaan proyek)";
  if (entry?.is_retention_claim) return "Klaim retensi — dihitung terpisah di Retensi & Collection";
  if (hideTest && isTestData(project, entry?.note || "")) return "Data TEST/DEMO — disembunyikan dari ringkasan";
  return null;
}

/**
 * Laporan rekonsiliasi penerimaan per invoice: nilai, sumber (riwayat/legacy),
 * status hitung, dan penyebab selisih bila dieksklusi. Total `total` dijamin
 * sama dengan "Sudah Diterima" Ringkasan untuk universe yang sama. Read-only.
 */
export function buildReceiptReconciliation(invoiceRows, opts = {}) {
  const rows = [];
  let riwayat = 0, legacy = 0, nRiwayat = 0, nLegacy = 0;
  for (const e of invoiceRows || []) {
    const receipts = e.payment_receipts || [];
    const isLegacy = isLegacyReceived(e);
    const received = receipts.length > 0
      ? receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0)
      : isLegacy ? computeExpectedNet(e) : 0;
    if (received <= 0) continue;
    const reason = incomeExclusionReason(e, e.__project, opts);
    const source = receipts.length > 0 ? "riwayat" : "legacy";
    rows.push({ id: e.id, entry: e, project: e.__project, source, amount: received, counted: !reason, reason });
    if (!reason) {
      if (source === "riwayat") { riwayat += received; nRiwayat += 1; }
      else { legacy += received; nLegacy += 1; }
    }
  }
  const excluded = rows.filter((r) => !r.counted);
  return { rows, excluded, riwayat, legacy, nRiwayat, nLegacy, total: riwayat + legacy };
}

/**
 * Deteksi kemungkinan duplikat expense berdasarkan source_ref/source_id.
 * Return Map<source_id_key, [expenseRow[]]> untuk yang duplikat (>=2 entry).
 */
export function detectExpenseDuplicates(expenseRows) {
  const bySourceId = new Map();
  for (const e of expenseRows) {
    // Prefer explicit source_ref/source_id/receipt_no, fallback ke combo (project+amount+date).
    const sourceKey = e.source_ref || e.source_id || e.receipt_no;
    if (!sourceKey) continue;
    const key = `${e.__project?.id || "no-project"}::${sourceKey}`;
    const list = bySourceId.get(key) || [];
    list.push(e);
    bySourceId.set(key, list);
  }
  const dupes = new Map();
  for (const [k, list] of bySourceId.entries()) {
    if (list.length > 1) dupes.set(k, list);
  }
  return dupes;
}

/**
 * Deteksi kemungkinan proyek duplikat: same project_name + client tetapi id berbeda.
 * Read-only warning — jangan auto-merge.
 */
export function detectProjectDuplicates(projects) {
  const byKey = new Map();
  for (const p of projects) {
    const name = (p.project_name || "").trim().toLowerCase();
    const client = (p.client || "").trim().toLowerCase();
    if (!name) continue;
    const key = `${name}::${client}`;
    const list = byKey.get(key) || [];
    list.push(p);
    byKey.set(key, list);
  }
  const dupes = [];
  for (const [k, list] of byKey.entries()) {
    if (list.length > 1) dupes.push({ key: k, projects: list });
  }
  return dupes;
}

function fmtShort(n) {
  const abs = Math.abs(n);
  if (abs >= 1e9) return `Rp ${(n / 1e9).toFixed(1)}M`;
  if (abs >= 1e6) return `Rp ${(n / 1e6).toFixed(1)}jt`;
  if (abs >= 1e3) return `Rp ${(n / 1e3).toFixed(0)}rb`;
  return `Rp ${n.toFixed(0)}`;
}
